import React, { useMemo } from 'react';
import { PanelContent } from '../common/Common';
import NavLink from 'next/link';
import { EnrollmentsOnlyQuery } from '@graphql/generated-graphql';
import { useRouter } from 'next/router';
import { urlTag } from '@utils/urlPaths';
import { SimpleSpinner } from '@components/common/loading/SimpleSpinner';
import { useEnrollHandler } from '@hooks/useEnrollmentsWithMapping';

export function CoursePanel() {
    const { enrolls, loading } = useEnrollHandler();
    const router = useRouter();
    const routePath = useMemo(() => {
        const currRouter = router.pathname.replace('/', '').split('/');
        let pathBuild = [];

        for (let i = 0; i < currRouter.length; i++) {
            const k = currRouter[i];
            if (currRouter[0] !== 'courses') {
                return urlTag`/courses/${'id'}`;
            }
            if (k === 'units') {
                break;
            }
            if (k === 'courses' || k === '[id]') {
                continue;
            }
            pathBuild.push(k);
        }
        return urlTag`/courses/${'id'}/${pathBuild.join('/')}`;
    }, [router.pathname]);

    let columnsSpan = 0;
    if (enrolls.leadingCourses.length > 0) {
        columnsSpan += 2;
    }
    if (enrolls.observingCouses.length > 0) {
        columnsSpan += 2;
    }
    if (enrolls.takingCourses.length > 0) {
        columnsSpan += 2;
    }

    return (
        <PanelContent
            columnsSpan={columnsSpan}
            renderBody={() => (
                <React.Fragment>
                    {loading && (
                        <div className="col-span-2 flex items-center justify-center p-10 z-0">
                            <SimpleSpinner />
                        </div>
                    )}
                    {!loading && (
                        <React.Fragment>
                            <CoursePanelList
                                data={enrolls.leadingCourses ?? []}
                                title="Courses I'm Leading"
                                routePath={routePath}
                                role="leader"
                            />
                            <CoursePanelList
                                data={enrolls.observingCouses ?? []}
                                title="Courses I'm Observing"
                                routePath={routePath}
                                role="parent"
                            />
                            <CoursePanelList
                                data={enrolls.takingCourses ?? []}
                                title="Courses I'm Taking"
                                routePath={routePath}
                                role="student"
                            />
                        </React.Fragment>
                    )}
                </React.Fragment>
            )}
            renderFooter={() => (
                <NavLink href="/">
                    <a className="px-6 py-2 flow-root rounded-md hover:bg-teal-050">
                        <div className="flex items-center">
                            <div className="text-base font-medium text-teal-700">VIEW ALL</div>
                        </div>
                    </a>
                </NavLink>
            )}
        />
    );
}

function CoursePanelList({
    data,
    title,
    routePath,
    role = '',
}: {
    data: EnrollmentsOnlyQuery['enrollments'];
    title: string;
    routePath: (obj: { [key: string]: string }) => string;
    role?: string;
}) {
    if (data?.length === 0) {
        return null;
    }

    return (
        <div className="relative col-span-2 h-full z-50 bg-white">
            <div className="text-lg whitespace-nowrap px-4 py-2 text-passport">{title}</div>
            <ul className="max-h-80 overflow-hidden overflow-y-auto md:overflow-visible max-w-xs ">
                {data?.map((e) => {
                    const c = e.course;
                    return (
                        <NavLink href={`${routePath({ id: c.id })}?role=${role}`} key={c.id}>
                            <li className="py-1.5 pl-2 pr-16 flex hover:bg-teal-050 overflow-hidden truncate cursor-pointer">
                                <div className="ml-1 flex items-center justify-center">
                                    <div
                                        className="h-3 w-3 rounded-full  mr-2"
                                        style={{
                                            backgroundColor: c.iconColor ?? '#c61f50',
                                        }}
                                    />
                                    <p className="text-base font-medium text-passport whitespace-nowrap">
                                        {c.title}
                                    </p>
                                </div>
                            </li>
                        </NavLink>
                    );
                })}
            </ul>
        </div>
    );
}
